/**
 * /oauth/google/callback
 *
 * The backend completes the OAuth dance and redirects here with:
 *   ?success=true|false
 *   &error=<short_error_code>   (only when success=false)
 *
 * We surface a toast and bounce the user back to Profile Settings →
 * Cloud Storage tab. The tab refetches its status and renders the
 * connected/disconnected state.
 *
 * Visually: minimal centered glass card on the auth gradient so it
 * doesn't feel like a different app for the half-second it's visible.
 */

import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useToast } from '../../components/common/ToastContainer'
import { Spinner } from '../../components/ui'
import { AuthShell, AuthGlassCard } from '../../components/auth'

const OAuthCallbackPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const success = params.get('success') === 'true'
    const error = params.get('error') || ''

    if (success) {
      toast?.success?.('Google Drive connected successfully', 'Cloud storage')
    } else {
      const message = humanizeOAuthError(error)
      toast?.error?.(message, 'Cloud storage connection failed')
    }

    navigate('/profile-settings?tab=cloud-storage', { replace: true })
  }, [location.search, navigate, toast])

  return (
    <AuthShell variant="centered">
      <AuthGlassCard className="max-w-md mx-auto text-center">
        <Spinner size="lg" className="mx-auto mb-5" />
        <p className="text-base font-semibold text-ink-primary">
          Completing Google Drive connection…
        </p>
        <p className="text-sm text-ink-secondary mt-1.5">
          You'll be redirected back to settings in a moment.
        </p>
      </AuthGlassCard>
    </AuthShell>
  )
}

function humanizeOAuthError(code) {
  switch (code) {
    case 'invalid_state':
      return 'Connection link expired. Please start the connection process again.'
    case 'no_refresh_token':
      return 'Google did not return offline access. Try disconnecting at myaccount.google.com → Security → Third-party apps, then reconnect.'
    case 'token_exchange_failed':
      return 'Failed to exchange the authorization code with Google. Please try again.'
    case 'drive_bootstrap_failed':
      return 'Could not create the OVACS folder in your Google Drive. Check that your Drive has storage available and try again.'
    case 'feature_disabled':
      return 'Cloud storage is not enabled on this OVACS instance.'
    case 'not_owner':
      return 'Only the workspace owner can connect cloud storage.'
    case 'persist_failed':
      return 'Connection succeeded with Google but could not be saved. Please contact support.'
    case 'access_denied':
      return 'You declined to grant access. Reconnect when ready.'
    default:
      return code ? `Connection failed: ${code}` : 'Connection failed. Please try again.'
  }
}

export default OAuthCallbackPage
